import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  Button,
  Grid,
  Chip,
  Divider,
  Rating,
  Avatar,
  Paper,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import PaymentDialog from '../components/PaymentDialog';
import StripeActivationDialog from '../components/StripeActivationDialog';
import {
  ArrowBack,
  Favorite,
  Share,
  CheckCircle,
  Info,
  LocationOn,
  Close,
  Security,
  NavigateBefore,
  NavigateNext
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isStripeAccountActive, getStripeInfo } from '../utils/stripeUtils';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { isDressLinkedToAccount } from '../utils/dressOwnerUtils';

// Interface pour les données de la robe
interface DressDetail {
  id: string;
  title: string;
  description: string;
  images: string[];
  price: number;
  originalPrice: number;
  size: string;
  material: string;
  condition: string;
  brand: string;
  color: string;
  style: string;
  events: string[];
  availability: {
    startDate: string;
    endDate: string;
    available: boolean;
  }[];
  availablePeriods: {
    start: string;
    end: string;
  }[];
  deposit: number;
  insurance: boolean;
  location: string;
  owner: {
    name: string;
    avatar: string;
    rating: number;
    verified: boolean;
  };
  reviews: {
    id: string;
    user: string;
    rating: number;
    comment: string;
    date: string;
    verified: boolean;
  }[];
  features: string[];
  careInstructions: string[];
  rentalConditions?: {
    cleaning: string;
    deposit: string;
    deliveryMethod: string;
    deliveryAddress: string;
    return: string;
    additional: string;
  };
}

const DressDetailScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const accentSoft = alpha(theme.palette.primary.main, 0.22);
  const { id } = useParams<{ id: string }>();
  const [dress, setDress] = useState<DressDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedTab, setSelectedTab] = useState(0);
  const [openBookingDialog, setOpenBookingDialog] = useState(false);
  const [openImageGallery, setOpenImageGallery] = useState(false);
  const [galleryImageIndex, setGalleryImageIndex] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookingData, setBookingData] = useState({
    startDate: '',
    endDate: '',
    deliveryMethod: 'pickup',
    specialRequests: ''
  });
  const [isLiked, setIsLiked] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectingPeriod, setSelectingPeriod] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [showStripeDialog, setShowStripeDialog] = useState(false);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [ownerStripeActive, setOwnerStripeActive] = useState<boolean | null>(null);

  // Charger les données depuis Firestore
  useEffect(() => {
    const loadDressData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const db = getFirestore();
        const dressDoc = await getDoc(doc(db, 'dresses', id));
        
        if (dressDoc.exists()) {
          const dressData = dressDoc.data();
          const link = await isDressLinkedToAccount(db, dressData as Record<string, unknown>);
          if (!link.valid || !link.ownerUid) {
            setDress(null);
            setLoading(false);
            return;
          }
          const ownerUserSnap = await getDoc(doc(db, 'users', link.ownerUid));
          const ownerProfile = ownerUserSnap.data();
          
          // Mapper les données Firestore vers la structure DressDetail
          const mappedDress: DressDetail = {
            id: dressDoc.id,
            title: dressData.title || dressData.name || 'Robe sans titre',
            description: dressData.description || '',
            images: dressData.images || (dressData.image ? [dressData.image] : []),
            price: dressData.price || 0,
            originalPrice: dressData.originalPrice || dressData.price * 5 || 0,
            size: dressData.size || '',
            material: dressData.material || '',
            condition: dressData.condition || 'Bon état',
            brand: dressData.brand || dressData.marque || '',
            color: dressData.color || '',
            style: dressData.style || '',
            events: dressData.events || [],
            availability: dressData.availability || [],
            availablePeriods: dressData.availablePeriods || [],
            deposit: dressData.deposit || 0,
            insurance: dressData.insurance || false,
            location: dressData.location || '',
            owner: {
              name:
                dressData.ownerName ||
                dressData.owner ||
                ownerProfile?.displayName ||
                ownerProfile?.name ||
                (typeof ownerProfile?.email === 'string'
                  ? ownerProfile.email.split('@')[0]
                  : '') ||
                'Propriétaire',
              avatar: dressData.ownerAvatar || ownerProfile?.photoURL || '',
              rating: dressData.ownerRating || 0,
              verified: dressData.ownerVerified || false
            },
            reviews: dressData.reviews || [],
            features: dressData.features || [],
            careInstructions: dressData.careInstructions || [],
            rentalConditions: dressData.rentalConditions || {
              cleaning: '',
              deposit: '',
              deliveryMethod: '',
              deliveryAddress: '',
              return: '',
              additional: ''
            }
          };
          
          setDress(mappedDress);
          setOwnerId(link.ownerUid);
        } else {
          // Robe non trouvée
          setDress(null);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la robe:', error);
        setDress(null);
      } finally {
        setLoading(false);
      }
    };

    loadDressData();
  }, [id]);

  // Afficher un message de chargement ou d'erreur
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Chargement de la robe...</Typography>
      </Box>
    );
  }

  if (!dress) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Annonce introuvable ou plus disponible
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 420, mx: 'auto' }}>
          Cette robe n&apos;est pas associée à un compte actif, ou l&apos;annonce a été retirée.
        </Typography>
        <Button onClick={() => navigate('/dashboard')} variant="contained">
          Retour au tableau de bord
        </Button>
      </Box>
    );
  }

  const handleImageZoom = (index: number) => {
    setGalleryImageIndex(index);
    setOpenImageGallery(true);
  };

  const handlePreviousImage = () => {
    setGalleryImageIndex((prev) => 
      prev === 0 ? dress.images.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setGalleryImageIndex((prev) => 
      prev === dress.images.length - 1 ? 0 : prev + 1
    );
  };

  const handleCloseGallery = () => {
    setOpenImageGallery(false);
  };




  const handleBooking = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Vérifier si l'utilisateur a Stripe activé
    try {
      const userStripeActive = await isStripeAccountActive(user.uid);
      if (!userStripeActive) {
        // Afficher le dialog d'activation Stripe (obligatoire)
        setShowStripeDialog(true);
        return;
      }
    } catch (error) {
      console.error('Erreur vérification Stripe utilisateur:', error);
      setShowStripeDialog(true);
      return;
    }

    // Note: La vérification du Stripe du propriétaire se fait dans MarketplacePaymentForm
    // Ici on vérifie seulement que l'utilisateur qui loue a Stripe activé

    // Ouvrir le dialog de paiement
    setOpenPaymentDialog(true);
    setOpenBookingDialog(false);
  };

  const handlePaymentSuccess = (paymentData: any) => {
    console.log('Paiement réussi:', paymentData);
    setPaymentSuccess(true);
    setOpenPaymentDialog(false);
    
    // Simulation de la création de la réservation
    setTimeout(() => {
      setPaymentSuccess(false);
      // Ici on pourrait rediriger vers une page de confirmation ou mettre à jour l'état global
    }, 3000);
  };

  // Lecture des infos de réservation passées via l'URL (depuis Mes Locations)
  const searchParams = new URLSearchParams(window.location.search);
  const incomingBooking = {
    bookingId: searchParams.get('bookingId') || undefined,
    start: searchParams.get('start') || undefined,
    end: searchParams.get('end') || undefined
  };

  const calculateDuration = () => {
    if (selectedStartDate && selectedEndDate) {
      const start = new Date(selectedStartDate);
      const end = new Date(selectedEndDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return 0;
  };

  const totalPrice = dress.price; // Prix fixe par location

  // Générer les dates du calendrier
  const generateCalendarDates = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 90; i++) { // 3 mois
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const calendarDates = generateCalendarDates();

  // Vérifier si une date est dans une période disponible
  const isDateInAvailablePeriod = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    return dress.availablePeriods?.some(period => 
      dateString >= period.start && dateString <= period.end
    ) || false;
  };

  // Vérifier si une date est sélectionnée
  const isDateSelected = (date: Date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  // Vérifier si une date est le début de sélection
  const isStartDate = (date: Date) => {
    return selectedStartDate && date.getTime() === selectedStartDate.getTime();
  };

  // Vérifier si une date est la fin de sélection
  const isEndDate = (date: Date) => {
    return selectedEndDate && date.getTime() === selectedEndDate.getTime();
  };

  // Vérifier si une date est en cours de sélection temporaire
  const isTempStartDate = (date: Date) => {
    return tempStartDate && date.getTime() === tempStartDate.getTime();
  };

  // Gérer le clic sur une date
  const handleDateClick = (date: Date) => {
    if (!isDateInAvailablePeriod(date)) return;

    if (!selectingPeriod) {
      // Premier clic : commencer la sélection
      setTempStartDate(date);
      setSelectingPeriod(true);
    } else {
      // Deuxième clic : terminer la sélection
      let startDate: Date;
      let endDate: Date;
      
      if (tempStartDate && date > tempStartDate) {
        startDate = tempStartDate;
        endDate = date;
      } else if (tempStartDate && date < tempStartDate) {
        startDate = date;
        endDate = tempStartDate;
        } else {
        // Même date : sélection d'un jour
        startDate = date;
        endDate = date;
      }
      
      setSelectedStartDate(startDate);
      setSelectedEndDate(endDate);
      
      // Mettre à jour bookingData avec les dates sélectionnées
      setBookingData(prev => ({
        ...prev,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      }));
      
      setSelectingPeriod(false);
      setTempStartDate(null);
    }
  };

  // Changer de mois
  const handleMonthChange = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 4 },
        backgroundColor: 'background.default',
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          aria-label="Retour"
          sx={{
            mr: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.primary',
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="overline"
          sx={{ color: 'primary.main', flexGrow: 1, letterSpacing: '0.28em', fontWeight: 600 }}
        >
          Fiche produit
        </Typography>
        <IconButton
          onClick={() => setIsLiked(!isLiked)}
          sx={{ color: isLiked ? 'error.main' : 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1, ml: 0.5 }}
        >
          <Favorite />
        </IconButton>
        <IconButton sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, ml: 0.5 }}>
          <Share />
        </IconButton>
      </Box>

      <Grid container spacing={{ xs: 3, md: 5 }}>
        {/* Section Photos */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              borderRadius: 2,
              bgcolor: '#fff',
            }}
          >
            <CardMedia
              component="img"
              image={dress.images[selectedImage]}
              alt={dress.title}
              sx={{
                cursor: 'pointer',
                width: '100%',
                aspectRatio: { xs: '3/4', md: '4/5' },
                maxHeight: { md: 640 },
                height: 'auto',
                objectFit: 'cover',
                display: 'block',
              }}
              onClick={() => handleImageZoom(selectedImage)}
            />
            
            {/* Miniatures */}
            <Box sx={{ display: 'flex', p: { xs: 1.5, sm: 2 }, gap: 1, overflowX: 'auto', bgcolor: alpha(theme.palette.background.default, 0.5) }}>
              {dress.images.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    minWidth: 80,
                    height: 80,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: selectedImage === index ? 'primary.main' : 'transparent',
                    opacity: selectedImage === index ? 1 : 0.7
                  }}
                  onClick={() => {
                    setSelectedImage(index);
                    handleImageZoom(index);
                  }}
                >
                  <img
                    src={image}
                    alt={`${dress.title} ${index + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>

        {/* Section Informations */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: { xs: 2, md: 4 } }}>
            <Typography
              variant="overline"
              sx={{ letterSpacing: '0.28em', color: 'text.secondary', display: 'block', mb: 1.5 }}
            >
              Location
            </Typography>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontFamily: '"Cormorant Garamond", serif',
                fontWeight: 500,
                color: 'text.primary',
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                lineHeight: 1.15,
              }}
            >
              {dress.title}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
                {dress.price} €
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                  / jour
                </Typography>
              </Typography>
              <Chip
                label="Estimation IA"
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: alpha(theme.palette.secondary.dark, 0.35),
                  bgcolor: alpha(theme.palette.secondary.light, 0.5),
                  color: 'text.secondary',
                  fontWeight: 500,
                  letterSpacing: '0.06em',
                }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520, lineHeight: 1.75 }}>
              Prix d&apos;achat original : {dress.originalPrice} € (location indicée à 20 % du prix d&apos;achat).
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.85, color: 'text.primary' }}>
              {dress.description}
            </Typography>

            {/* Caractéristiques */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Taille</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{dress.size}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Matière</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{dress.material}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">État</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{dress.condition}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Marque</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{dress.brand}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Localisation</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <LocationOn sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} />
                  {dress.location}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Couleur</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{dress.color}</Typography>
              </Grid>
            </Grid>

            {/* Événements conseillés */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Événements conseillés
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {dress.events.map((event, index) => (
                  <Chip key={index} label={event} size="small" />
                ))}
              </Box>
            </Box>

            {/* Bouton de réservation */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={() => setOpenBookingDialog(true)}
              sx={{
                py: 1.75,
                mb: 2,
                letterSpacing: '0.14em',
                fontSize: '0.8rem',
              }}
            >
              Louer cette robe
            </Button>

            {/* Informations de sécurité + résumé éventuel de réservation transmis */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Security sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Caution: {dress.deposit}€
                  </Typography>
                  <Typography variant="body2">
                    Assurance incluse: {dress.insurance ? 'Oui' : 'Non'}
                  </Typography>
                  {(incomingBooking.start || incomingBooking.end) && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Période réservée: {incomingBooking.start ? new Date(incomingBooking.start).toLocaleDateString('fr-FR') : '?'} → {incomingBooking.end ? new Date(incomingBooking.end).toLocaleDateString('fr-FR') : '?'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Alert>

            <Button
              variant="outlined"
              fullWidth
              disabled={!ownerId}
              sx={{ mb: 2, borderColor: 'primary.main', color: 'primary.main' }}
              onClick={() => { 
                if (!ownerId) return;
                window.location.href = `/dashboard/chat?ownerId=${encodeURIComponent(ownerId)}&dressId=${encodeURIComponent(dress.id)}`;
              }}
            >
              Ouvrir le chat avec le propriétaire
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Tabs pour plus d'informations */}
      <Box sx={{ mt: 4 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label="Disponibilités" />
          <Tab label="Avis clients" />
          <Tab label="Propriétaire" />
          <Tab label="Entretien" />
          <Tab label="Conditions de prêt" />
        </Tabs>

        {/* Contenu des tabs */}
        <Box sx={{ mt: 3 }}>
          {selectedTab === 0 && (
            <Paper sx={{ p: 2, maxWidth: 600, mx: 'auto' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                Sélectionner les dates
              </Typography>
              
              {/* Résumé de sélection */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>
                  {selectedStartDate && selectedEndDate ? 
                    `${calculateDuration()} jour${calculateDuration() > 1 ? 's' : ''}` : 
                    'Sélectionnez vos dates'
                  }
                </Typography>
                {selectedStartDate && selectedEndDate && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedStartDate.toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })} - {selectedEndDate.toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </Typography>
                )}
              </Box>

              {/* Calendrier de disponibilité */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  Calendrier de disponibilité
                </Typography>
                
                {/* Navigation du calendrier */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Button 
                    onClick={() => handleMonthChange('prev')}
                    variant="outlined"
                    size="small"
                  >
                    ← Précédent
                  </Button>
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>
                    {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </Typography>
                  <Button 
                    onClick={() => handleMonthChange('next')}
                    variant="outlined"
                    size="small"
                  >
                    Suivant →
                  </Button>
                </Box>

                {/* Grille du calendrier */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(7, 1fr)', 
                  gap: 1,
                  mb: 2
                }}>
                  {/* En-têtes des jours */}
                  {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
                    <Typography key={day} variant="body2" sx={{ textAlign: 'center', fontWeight: 'bold', p: 1 }}>
                      {day}
                    </Typography>
                  ))}
                  
                  {/* Dates du calendrier */}
                  {calendarDates
                    .filter(date => date.getMonth() === currentMonth.getMonth() && date.getFullYear() === currentMonth.getFullYear())
                    .map(date => {
                      const isAvailable = isDateInAvailablePeriod(date);
                      const isSelected = isDateSelected(date);
                      const isStart = isStartDate(date);
                      const isEnd = isEndDate(date);
                      const isTempStart = isTempStartDate(date);
                      const isToday = date.toDateString() === new Date().toDateString();
                      const isPast = date < new Date();
                      
                      return (
                        <Button
                          key={date.toISOString()}
                          onClick={() => handleDateClick(date)}
                          disabled={!isAvailable || isPast}
                          sx={{
                            minWidth: 40,
                            height: 40,
                            borderRadius: 2,
                            backgroundColor: isSelected ? 'primary.main' : 
                                           isTempStart ? accentSoft :
                                           isAvailable ? '#f0f0f0' : '#e0e0e0',
                            color: isSelected ? 'white' : 
                                   isAvailable ? '#333' : '#999',
                            border: isStart || isEnd ? `2px solid ${theme.palette.primary.main}` : 
                                    isToday ? '2px solid #1976d2' : 'none',
                            '&:hover': {
                              backgroundColor: isAvailable ? accentSoft : '#e0e0e0',
                            },
                            '&:disabled': {
                              backgroundColor: '#f5f5f5',
                              color: '#ccc',
                            }
                          }}
                        >
                          {date.getDate()}
                        </Button>
                      );
                    })}
                </Box>

                {/* Légende */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: 'primary.main', borderRadius: 1 }} />
                    <Typography variant="caption">Disponible</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
                    <Typography variant="caption">Indisponible</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: 'primary.main', borderRadius: 1, border: '2px solid', borderColor: 'primary.main' }} />
                    <Typography variant="caption">Sélectionné</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Bouton de réservation */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  disabled={!selectedStartDate || !selectedEndDate}
                  onClick={() => {
                    setBookingData(prev => ({
                      ...prev,
                      startDate: selectedStartDate?.toISOString().split('T')[0] || '',
                      endDate: selectedEndDate?.toISOString().split('T')[0] || ''
                    }));
                    setOpenBookingDialog(false);
                    setOpenPaymentDialog(true);
                  }}
                  sx={{
                    backgroundColor: 'primary.main',
                    '&:hover': { backgroundColor: 'primary.dark' },
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem'
                  }}
                >
                  Réserver maintenant - {dress.price}€
                </Button>
              </Box>

              {/* Actions */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="text"
                  onClick={() => {
                    setBookingData(prev => ({ ...prev, startDate: '', endDate: '' }));
                    setSelectedStartDate(null);
                    setSelectedEndDate(null);
                  }}
                  sx={{ color: 'primary.main' }}
                >
                  Effacer les dates
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setOpenBookingDialog(true)}
                  disabled={!bookingData.startDate || !bookingData.endDate}
                  sx={{ 
                    backgroundColor: 'primary.main', 
                    '&:hover': { backgroundColor: 'primary.dark' },
                    borderRadius: 2
                  }}
                >
                  Confirmer
                </Button>
              </Box>
            </Paper>
          )}

          {selectedTab === 1 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Avis clients ({dress.reviews.length})
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Rating value={4.7} precision={0.1} readOnly />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  4.7/5 basé sur {dress.reviews.length} avis
                </Typography>
              </Box>
              
              {dress.reviews.map((review) => (
                <Box key={review.id} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, mr: 2 }}>
                      {review.user.charAt(0)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {review.user}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Rating value={review.rating} size="small" readOnly />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          {new Date(review.date).toLocaleDateString('fr-FR')}
                        </Typography>
                        {review.verified && (
                          <CheckCircle sx={{ ml: 1, fontSize: 16, color: '#4caf50' }} />
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="body2">{review.comment}</Typography>
                </Box>
              ))}
            </Paper>
          )}

          {selectedTab === 2 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Informations propriétaire
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar src={dress.owner.avatar} sx={{ width: 60, height: 60, mr: 2 }} />
                <Box>
                  <Typography 
                    variant="h6"
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => {
                      // Si vous avez l'ownerId dans les données de la robe
                      // navigate(`/user/${dress.ownerId || dress.userId}`);
                      // Sinon, vous devrez le récupérer depuis Firestore
                      console.log('Cliquer sur le nom pour voir le profil public');
                    }}
                  >
                    {dress.owner.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Rating value={dress.owner.rating} precision={0.1} size="small" readOnly />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {dress.owner.rating}/5
                    </Typography>
                    {dress.owner.verified && (
                      <CheckCircle sx={{ ml: 1, fontSize: 20, color: '#4caf50' }} />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    <LocationOn sx={{ fontSize: 16, mr: 0.5 }} />
                    {dress.location}
                  </Typography>
                </Box>
              </Box>
              
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  ✓ Propriétaire vérifié et fiable
                </Typography>
                <Typography variant="body2">
                  ✓ Plus de 50 locations réussies
                </Typography>
                <Typography variant="body2">
                  ✓ Réponse rapide aux messages
                </Typography>
              </Alert>
            </Paper>
          )}

          {selectedTab === 3 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Instructions d'entretien
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Caractéristiques spéciales
                </Typography>
                <List dense>
                  {dress.features.map((feature, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#4caf50' }} />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Instructions de soin
                </Typography>
                <List dense>
                  {dress.careInstructions.map((instruction, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Info sx={{ color: '#2196f3' }} />
                      </ListItemIcon>
                      <ListItemText primary={instruction} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Paper>
          )}

          {selectedTab === 4 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                📋 Conditions de prêt
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Conditions spécifiques définies par le propriétaire pour la location de cette robe
              </Typography>
              
              {dress.rentalConditions ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        🧽 Nettoyage
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                        {dress.rentalConditions.cleaning}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        💰 Caution
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                        {dress.rentalConditions.deposit}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        📦 Mode de remise
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                        {dress.rentalConditions.deliveryMethod === 'pickup' && 'Remise en main propre uniquement'}
                        {dress.rentalConditions.deliveryMethod === 'delivery' && 'Livraison uniquement'}
                        {dress.rentalConditions.deliveryMethod === 'both' && 'Remise en main propre ou livraison'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {(dress.rentalConditions.deliveryMethod !== 'pickup') && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          📍 Adresse de livraison/retrait
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                          {dress.rentalConditions.deliveryAddress}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        🔄 Retour
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                        {dress.rentalConditions.return}
                      </Typography>
                    </Box>

                    {dress.rentalConditions.additional && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          ⚠️ Conditions supplémentaires
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                          {dress.rentalConditions.additional}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              ) : (
                <Alert severity="info">
                  Aucune condition de prêt spécifique définie par le propriétaire.
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Important :</strong> Ces conditions sont définies par le propriétaire. 
                  En réservant cette robe, vous acceptez de respecter toutes les conditions énoncées ci-dessus.
                  En cas de non-respect, des frais supplémentaires pourront être appliqués.
                </Typography>
              </Alert>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Dialog de réservation */}
      <Dialog 
        open={openBookingDialog} 
        onClose={() => {
          setOpenBookingDialog(false);
          // Réinitialiser la sélection si l'utilisateur ferme sans confirmer
          if (!selectedStartDate || !selectedEndDate) {
            setTempStartDate(null);
            setSelectingPeriod(false);
          }
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Louer cette robe</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Calendrier de sélection de dates */}
          <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                Sélectionner les dates
              </Typography>
              
              {/* Résumé de sélection */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>
                  {selectedStartDate && selectedEndDate ? 
                    `${calculateDuration()} jour${calculateDuration() > 1 ? 's' : ''}` : 
                    'Sélectionnez vos dates'
                  }
                </Typography>
                {selectedStartDate && selectedEndDate && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedStartDate.toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })} - {selectedEndDate.toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </Typography>
                )}
              </Box>

              {/* Calendrier de disponibilité */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  Calendrier de disponibilité
                </Typography>
                
                {/* Navigation du calendrier */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Button 
                    onClick={() => handleMonthChange('prev')}
                    variant="outlined"
                    size="small"
                  >
                    ← Précédent
                  </Button>
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>
                    {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </Typography>
                  <Button 
                    onClick={() => handleMonthChange('next')}
                    variant="outlined"
                    size="small"
                  >
                    Suivant →
                  </Button>
                </Box>

                {/* Grille du calendrier */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(7, 1fr)', 
                  gap: 1,
                  mb: 2
                }}>
                  {/* En-têtes des jours */}
                  {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
                    <Typography key={day} variant="body2" sx={{ textAlign: 'center', fontWeight: 'bold', p: 1 }}>
                      {day}
                    </Typography>
                  ))}
                  
                  {/* Dates du calendrier */}
                  {calendarDates
                    .filter(date => date.getMonth() === currentMonth.getMonth() && date.getFullYear() === currentMonth.getFullYear())
                    .map(date => {
                      const isAvailable = isDateInAvailablePeriod(date);
                      const isSelected = isDateSelected(date);
                      const isStart = isStartDate(date);
                      const isEnd = isEndDate(date);
                      const isTempStart = isTempStartDate(date);
                      const isToday = date.toDateString() === new Date().toDateString();
                      const isPast = date < new Date();
                      
                      return (
                        <Button
                          key={date.toISOString()}
                          onClick={() => handleDateClick(date)}
                          disabled={!isAvailable || isPast}
                          sx={{
                            minWidth: 40,
                            height: 40,
                            borderRadius: 2,
                            backgroundColor: isSelected ? 'primary.main' : 
                                           isTempStart ? accentSoft :
                                           isAvailable ? '#f0f0f0' : '#e0e0e0',
                            color: isSelected ? 'white' : 
                                   isAvailable ? '#333' : '#999',
                            border: isStart || isEnd ? `2px solid ${theme.palette.primary.main}` : 
                                    isToday ? '2px solid #1976d2' : 'none',
                            '&:hover': {
                              backgroundColor: isAvailable ? accentSoft : '#e0e0e0',
                            },
                            '&:disabled': {
                              backgroundColor: '#f5f5f5',
                              color: '#ccc',
                            }
                          }}
                        >
                          {date.getDate()}
                        </Button>
                      );
                    })}
                </Box>

                {/* Légende */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: 'primary.main', borderRadius: 1 }} />
                    <Typography variant="caption">Disponible</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
                    <Typography variant="caption">Indisponible</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, backgroundColor: 'primary.main', borderRadius: 1, border: '2px solid', borderColor: 'primary.main' }} />
                    <Typography variant="caption">Sélectionné</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Mode de livraison</InputLabel>
                <Select
                  value={bookingData.deliveryMethod}
                  onChange={(e) => setBookingData({ ...bookingData, deliveryMethod: e.target.value })}
                >
                  <MenuItem value="pickup">Récupération sur place</MenuItem>
                  <MenuItem value="delivery">Livraison à domicile (+15€)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Demandes spéciales"
                multiline
                rows={3}
                value={bookingData.specialRequests}
                onChange={(e) => setBookingData({ ...bookingData, specialRequests: e.target.value })}
                placeholder="Accessoires souhaités, taille particulière, etc."
              />
            </Grid>
          </Grid>

          {/* Bouton pour effacer les dates */}
          {(selectedStartDate || selectedEndDate) && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedStartDate(null);
                  setSelectedEndDate(null);
                  setTempStartDate(null);
                  setSelectingPeriod(false);
                  setBookingData(prev => ({
                    ...prev,
                    startDate: '',
                    endDate: ''
                  }));
                }}
                sx={{ mb: 2 }}
              >
                Effacer les dates
              </Button>
            </Grid>
          )}

          {/* Résumé de la réservation */}
          {selectedStartDate && selectedEndDate && (
            <Paper sx={{ p: 2, mt: 3, backgroundColor: '#f5f5f5' }}>
              <Typography variant="h6" gutterBottom>
                Résumé de la réservation
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Durée:</Typography>
                <Typography>{calculateDuration()} jour(s)</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Dates:</Typography>
                <Typography variant="body2">
                  {selectedStartDate.toLocaleDateString('fr-FR')} - {selectedEndDate.toLocaleDateString('fr-FR')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Prix de location:</Typography>
                <Typography>{dress.price}€</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Caution:</Typography>
                <Typography>{dress.deposit}€</Typography>
              </Box>
              {bookingData.deliveryMethod === 'delivery' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Livraison:</Typography>
                  <Typography>15€</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  {totalPrice + dress.deposit + (bookingData.deliveryMethod === 'delivery' ? 15 : 0)}€
                </Typography>
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBookingDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={() => {
              if (selectedStartDate && selectedEndDate) {
                // Mettre à jour bookingData avec les dates sélectionnées
                setBookingData(prev => ({
                  ...prev,
                  startDate: selectedStartDate.toISOString().split('T')[0],
                  endDate: selectedEndDate.toISOString().split('T')[0]
                }));
                handleBooking();
              }
            }}
            variant="contained"
            disabled={!selectedStartDate || !selectedEndDate}
            sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
          >
            Confirmer la réservation
        </Button>
        </DialogActions>
      </Dialog>

      {/* Galerie d'images en plein écran */}
      <Dialog
        open={openImageGallery}
        onClose={handleCloseGallery}
        maxWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '100vw',
            height: '100vh',
            margin: 0,
            borderRadius: 0
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', height: '100vh' }}>
          {/* Bouton fermer */}
          <IconButton
            onClick={handleCloseGallery}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <Close />
          </IconButton>

          {/* Bouton précédent */}
          <IconButton
            onClick={handlePreviousImage}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <NavigateBefore />
          </IconButton>

          {/* Bouton suivant */}
          <IconButton
            onClick={handleNextImage}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <NavigateNext />
          </IconButton>

          {/* Image principale */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
              width: '100vw'
            }}
          >
            <img
              src={dress.images[galleryImageIndex]}
              alt={`${dress.title} - ${galleryImageIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          </Box>

          {/* Indicateur de position */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '8px 16px',
              borderRadius: 2
            }}
          >
            {dress.images.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: index === galleryImageIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => setGalleryImageIndex(index)}
              />
            ))}
          </Box>

          {/* Compteur d'images */}
          <Typography
            variant="body2"
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '4px 8px',
              borderRadius: 1
            }}
          >
            {galleryImageIndex + 1} / {dress.images.length}
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Dialog de paiement */}
      <PaymentDialog
        open={openPaymentDialog}
        onClose={() => setOpenPaymentDialog(false)}
        dressData={{
          title: dress.title,
          price: dress.price,
          location: dress.location,
          owner: dress.owner.name,
          deposit: dress.deposit
        }}
        bookingData={bookingData}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Notification de succès */}
      {paymentSuccess && (
        <Dialog open={paymentSuccess} onClose={() => setPaymentSuccess(false)}>
          <DialogContent sx={{ textAlign: 'center', p: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Réservation confirmée !
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Votre paiement a été traité avec succès et votre réservation est confirmée.
            </Typography>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog d'activation Stripe (obligatoire pour louer) */}
      <StripeActivationDialog
        open={showStripeDialog}
        onClose={() => setShowStripeDialog(false)}
        onActivate={() => {
          // La redirection sera gérée dans le dialog
        }}
        required={true}
      />
    </Box>
  );
};

export default DressDetailScreen; 